import { readAllBytes, type Bridge } from '@casa/bridge';
import type { Log, LogEntry } from './log.ts';

// The opt-in remote log sink (plan step 1.11): the page's log, sent in batches to a URL the user
// sets in the settings, through the bridge. Off unless the user turns it on. Its target will be the
// Tilez Service's log module; until then any URL that takes a JSON POST. Entries are already masked
// (log.ts). A target that is down is tried again with back-off; entries the ring buffer drops
// meanwhile are counted in the next batch as `lost`.

export interface LogSinkSettings {
    url: string;
    enabled: boolean;
}

export const LOG_SINK_PATH = 'log-sink.json';
export const SEND_INTERVAL_MS = 30_000;
export const MAX_RETRY_MS = 300_000;
export const MAX_BATCH = 200;
const SEND_TIMEOUT_MS = 10_000;

/** What a batch is: the device it comes from, the entries, and how many were dropped before sending. */
export interface LogBatch {
    device: Record<string, string>;
    lost: number;
    entries: readonly LogEntry[];
}

export interface LogSinkOptions {
    log: Log;
    bridge: Bridge;
    url: string;
    /** Sent with every batch: shell, platform, model. */
    device: Record<string, string>;
    intervalMs?: number;
}

export interface LogSink {
    /** Sends what has not been sent; rejects when the target does not take it. */
    flush() : Promise<void>;
    stop() : void;
}

export function startLogSink(options: LogSinkOptions) : LogSink {
    const interval = options.intervalMs ?? SEND_INTERVAL_MS;
    let sentUpTo = 0;
    let delay = interval;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let sending: Promise<void> | undefined;

    const send = async () : Promise<void> => {
        const pending = options.log.entries().filter((entry) => entry.seq > sentUpTo);
        const batch = pending.slice(0, MAX_BATCH);
        const first = batch[0];
        const last = batch.at(-1);
        if (!first || !last) {
            return;
        }

        const body: LogBatch = { device: options.device, lost: first.seq - sentUpTo - 1, entries: batch };
        const response = await options.bridge.net.http.request({
            method: 'POST',
            url: options.url,
            headers: { 'content-type': 'application/json' },
            body: new TextEncoder().encode(JSON.stringify(body)),
            timeoutMs: SEND_TIMEOUT_MS,
        });
        await readAllBytes(response.body);
        if (response.status < 200 || response.status >= 300) {
            throw new Error(`the log sink answered ${response.status}`);
        }

        sentUpTo = last.seq;
    };

    const flush = () : Promise<void> => {
        sending ??= send().finally(() => {
            sending = undefined;
        });

        return sending;
    };

    const schedule = () : void => {
        if (stopped) {
            return;
        }

        timer = setTimeout(() => {
            void flush().then(
                () => {
                    delay = interval;
                },
                () => {
                    // Not logged: a log entry about the sink would be one more entry to send.
                    delay = Math.min(delay * 2, MAX_RETRY_MS);
                },
            ).finally(schedule);
        }, delay);
    };
    schedule();

    return {
        flush,
        stop() {
            stopped = true;
            clearTimeout(timer);
        },
    };
}

export async function readLogSinkSettings(bridge: Bridge) : Promise<LogSinkSettings> {
    const stored = await bridge.storage.read(LOG_SINK_PATH);
    if (!stored) {
        return { url: '', enabled: false };
    }

    try {
        const parsed = JSON.parse(new TextDecoder().decode(stored)) as Partial<LogSinkSettings>;

        return { url: typeof parsed.url === 'string' ? parsed.url : '', enabled: parsed.enabled === true };
    } catch {
        return { url: '', enabled: false };
    }
}

export async function writeLogSinkSettings(bridge: Bridge, settings: LogSinkSettings) : Promise<void> {
    await bridge.storage.write(LOG_SINK_PATH, new TextEncoder().encode(`${JSON.stringify(settings, null, 2)}\n`));
}
